import { findIndex } from 'lodash';
import { useEffect, useRef, useState } from 'react';
import {
  ChatSessionDto,
  FindAllOmniMessagesDto,
  GetOmniMessageResponseDto,
  MessagePosition,
  OmniChannelMessageDto,
  ReadOmniChannelMessageDto
} from 'types';
import { calculateStartEndPositionMessage, maskedMessageReplied, OMNI_MESSAGE_QUERY_TYPE } from 'utils';
import { useReadOmniChatMessagesMutation } from 'services';
import { QueryActionCreatorResult } from '@reduxjs/toolkit/query';
import { useReceiveOmniChatMessage } from './useReceiveOmniChatMessage';

type PropsType = {
  chatSession: ChatSessionDto;
  fetchMessage: (value: FindAllOmniMessagesDto) => QueryActionCreatorResult<any>;
  hasNew?: boolean;
  setLoading: (value: boolean) => void;
  isRead?: boolean;
};

export function useScrollOmniMessage({ chatSession, fetchMessage, hasNew, setLoading, isRead = true }: PropsType) {
  const { chatId, chatSessionId } = chatSession;
  const [isAtBottomScroll, setIsAtBottomScroll] = useState(true);
  const [displayIndex, setDisplayIndex] = useState({ start: 0, end: 50 });

  const messagesBoxRef = useRef<HTMLDivElement>(null);
  const messageListRef = useRef<HTMLDivElement>(null);
  const positionsRef = useRef<MessagePosition[]>([]);

  const [onReadMess] = useReadOmniChatMessagesMutation();

  const { handleReceiveOlderOmniMessageList } = useReceiveOmniChatMessage(chatSession);

  useEffect(() => {
    const div = messagesBoxRef?.current;
    if (div) {
      div.addEventListener('scroll', handleScroll);
    }
    return () => {
      if (div) {
        div.removeEventListener('scroll', handleScroll);
      }
    };
  }, [positionsRef.current, messagesBoxRef]);

  const handleReadAllMess = (data: ReadOmniChannelMessageDto) => {
    if (isRead) onReadMess(data).unwrap();
  };

  const changeHeightChatList = (height: number, scrollTop?: number) => {
    if (messageListRef.current) {
      messageListRef.current.style.height = `${height}px`;
    }
    if (messagesBoxRef.current && scrollTop) {
      messagesBoxRef.current.scrollTop = scrollTop;
    }
  };

  const handleScroll = () => {
    if (!messagesBoxRef?.current) return;
    const scrollTop = messagesBoxRef.current.scrollTop;
    const clientHeight = messagesBoxRef.current.clientHeight;
    const scrollHeight = messagesBoxRef.current.scrollHeight;
    setIsAtBottomScroll(scrollTop > scrollHeight - 2 * clientHeight);
    setDisplayIndex(
      calculateStartEndPositionMessage(
        {
          scrollTop,
          clientHeight
        },
        positionsRef.current
      )
    );
  };

  const scrollToMessage = (repliedMessage: OmniChannelMessageDto, color?: string) => {
    const replyMessIdx = findIndex(positionsRef.current, (o) => o.messageId === repliedMessage.messageId);
    if (replyMessIdx === -1) return;
    if (!messageListRef.current) return;

    const messReplied = positionsRef.current[replyMessIdx];
    messagesBoxRef.current?.scrollTo({
      top: (messReplied?.position ?? 0) - (messReplied?.height > 100 ? 0 : 100),
      behavior: 'smooth'
    });
    maskedMessageReplied(messReplied, messageListRef.current, color, () => setLoading(false));
  };

  const scrollToEndMessage = () => {
    if (hasNew) {
      setLoading(true);
      if (messageListRef.current?.offsetHeight) {
        messagesBoxRef?.current?.scrollTo({
          top: messageListRef.current?.offsetHeight,
          behavior: 'smooth'
        });
      }
      fetchMessage({
        chatId: chatId,
        startMessageId: undefined
      })
        .unwrap()
        .then(async (rs) => {
          const response = rs as GetOmniMessageResponseDto;

          const data = await handleReceiveOlderOmniMessageList(response.data.rows, null, []);
          if (data) {
            changeHeightChatList(data.height);
            positionsRef.current = data.positions;
            messagesBoxRef?.current?.scrollTo({
              top: data.height,
              behavior: 'smooth'
            });
          }
          handleReadAllMess({
            chatSessionId,
            messageIds: []
          });
        })
        .finally(() => {
          setTimeout(() => {
            setLoading(false);
          }, 500);
        });
    } else {
      if (messagesBoxRef.current) {
        messagesBoxRef?.current?.scrollTo({
          top: messagesBoxRef.current?.scrollHeight + 200,
          behavior: 'smooth'
        });
      }
    }
  };

  const scrollToMessageReplied = async (repliedMessage: OmniChannelMessageDto, color?: string) => {
    const replyMessIdx = findIndex(positionsRef.current, (o) => o.messageId === repliedMessage.messageId);
    if (replyMessIdx !== -1) {
      scrollToMessage(repliedMessage, color);
    } else {
      setLoading(true);
      messagesBoxRef.current?.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
      fetchRepliedMessage(repliedMessage, color);
    }
  };

  const fetchRepliedMessage = async (repliedMessage: OmniChannelMessageDto, color?: string) => {
    fetchMessage({
      chatId,
      sortOrder: OMNI_MESSAGE_QUERY_TYPE.NEWER,
      startMessageId: repliedMessage.messageId,
      limit: 30,
      message: repliedMessage
    })
      .unwrap()
      .then(async (rs) => {
        fetchMessage({
          chatId,
          limit: 20,
          startMessageId: repliedMessage.messageId
        })
          .unwrap()
          .then(async (rsPrev) => {
            const response = rs as GetOmniMessageResponseDto;
            const responsePrev = rsPrev as GetOmniMessageResponseDto;

            const newData = [...(responsePrev.newMess || []), ...(response.data.rows || [])];
            const data = await handleReceiveOlderOmniMessageList(newData, null, []);
            if (data) {
              changeHeightChatList(data.height);
              positionsRef.current = data.positions;
            }
            scrollToMessage(repliedMessage, color);
          });
      });
  };

  return {
    messagesBoxRef,
    messageListRef,
    positionsRef,
    isAtBottomScroll,
    displayIndex,
    scrollToEndMessage,
    scrollToMessageReplied,
    changeHeightChatList
  };
}
